import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Trophy,
  Medal,
  Award as AwardIcon,
  RefreshCw,
  Users as UsersIcon,
} from "lucide-react";

function currentMonthStr() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function lastNMonths(n) {
  const now = new Date();
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push({
      value: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }),
    });
  }
  return out;
}

const PODIUM_ICONS = {
  1: { Icon: Trophy, color: "text-amber-300", bg: "bg-amber-500/[0.10]", border: "border-amber-400/60", label: "Gold" },
  2: { Icon: Medal, color: "text-zinc-200", bg: "bg-zinc-300/[0.08]", border: "border-zinc-400/40", label: "Silver" },
  3: { Icon: AwardIcon, color: "text-orange-300", bg: "bg-orange-500/[0.08]", border: "border-orange-400/40", label: "Bronze" },
};

function PodiumCard({ entry, isMe }) {
  const meta = PODIUM_ICONS[entry.rank];
  const { Icon } = meta;
  return (
    <div
      data-testid={`podium-rank-${entry.rank}`}
      className={`relative rounded-2xl border ${meta.border} ${meta.bg} p-6 transition-all ${isMe ? "ring-2 ring-amber-400/60" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg border ${meta.border} bg-zinc-950/60 ${meta.color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${meta.color}`}>
          {meta.label} · #{entry.rank}
        </span>
      </div>
      <h3 className="mt-4 text-xl font-semibold text-zinc-50 truncate">{entry.name || "—"}</h3>
      {entry.founder_persona && (
        <p className="mt-1 text-xs text-zinc-500 truncate">{entry.founder_persona}</p>
      )}
      <div className="mt-5 grid grid-cols-3 gap-3">
        <div>
          <div className="font-mono text-lg text-amber-300">{entry.total_xp.toLocaleString()}</div>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-zinc-500">xp</div>
        </div>
        <div>
          <div className="font-mono text-lg text-zinc-200">{entry.quests_completed}</div>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-zinc-500">done</div>
        </div>
        <div>
          <div className="font-mono text-lg text-zinc-200">{Math.round(entry.avg_score)}</div>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-zinc-500">avg</div>
        </div>
      </div>
      {isMe && (
        <span className="absolute -top-2 -right-2 rounded-full border border-amber-400/60 bg-amber-500/20 px-2.5 py-0.5 text-[9px] font-mono uppercase tracking-widest text-amber-200">
          you
        </span>
      )}
    </div>
  );
}

function Row({ entry, isMe }) {
  return (
    <div
      data-testid={`leaderboard-row-${entry.rank}`}
      className={`grid grid-cols-[40px_1fr_auto] sm:grid-cols-[60px_1fr_auto_auto_auto] items-center gap-3 sm:gap-6 rounded-xl border px-3 sm:px-4 py-3 transition-all ${
        isMe
          ? "border-amber-400/60 bg-amber-500/[0.06]"
          : "border-zinc-800 bg-zinc-950/40 hover:bg-zinc-950/70"
      }`}
    >
      <span className="font-mono text-sm text-zinc-500">#{entry.rank}</span>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-zinc-100 truncate">
            {entry.name || "—"}
          </span>
          {isMe && (
            <span className="rounded-full border border-amber-400/50 bg-amber-500/[0.12] px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-amber-200">
              you
            </span>
          )}
          {entry.award_rank && (
            <span className="rounded-full border border-amber-400/50 bg-amber-500/[0.12] px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest text-amber-200">
              award · #{entry.award_rank}
            </span>
          )}
        </div>
        {entry.founder_persona && (
          <div className="mt-0.5 text-[11px] text-zinc-500 truncate">{entry.founder_persona}</div>
        )}
        {/* Mobile-only secondary line — collapses the dropped columns into a single small caption */}
        <div className="mt-1 text-[10px] font-mono text-zinc-500 sm:hidden">
          {entry.quests_completed} quest{entry.quests_completed === 1 ? "" : "s"} · avg {Math.round(entry.avg_score)}
        </div>
      </div>
      <span className="font-mono text-sm text-amber-300 tabular-nums whitespace-nowrap">
        {entry.total_xp.toLocaleString()}<span className="ml-1 text-[10px] text-zinc-600 uppercase">xp</span>
      </span>
      <span className="hidden sm:block font-mono text-xs text-zinc-400 tabular-nums whitespace-nowrap">
        {entry.quests_completed}<span className="ml-1 text-[10px] text-zinc-600">done</span>
      </span>
      <span className="hidden sm:block font-mono text-xs text-zinc-400 tabular-nums whitespace-nowrap">
        {Math.round(entry.avg_score)}<span className="ml-1 text-[10px] text-zinc-600">avg</span>
      </span>
    </div>
  );
}

export default function LeaderboardPage() {
  const [, navigate] = useLocation();
  const { user } = useUser();
  const [month, setMonth] = useState(currentMonthStr());
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .getLeaderboard(month)
      .then((r) => {
        if (!cancelled) {
          setEntries(r.entries || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load leaderboard.");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [month, refreshTick, user, navigate]);

  const months = useMemo(() => lastNMonths(6), []);
  const podium = (entries || []).slice(0, 3);
  const rest = (entries || []).slice(3);
  const myRank = useMemo(() => {
    if (!entries || !user) return null;
    const m = entries.find((e) => e.user_id === user.id);
    return m ? m.rank : null;
  }, [entries, user]);

  const monthLabel = months.find((m) => m.value === month)?.label || month;

  if (!user) return null;

  return (
    <main data-testid="leaderboard-page" className="min-h-screen px-6 sm:px-10 lg:px-20 py-12">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate("/dashboard")}
          data-testid="leaderboard-back-btn"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300">
              the leaderboard
            </p>
            <h1
              data-testid="leaderboard-title"
              className="mt-2 text-3xl md:text-5xl font-semibold tracking-tight text-zinc-50"
            >
              Founder Leaderboard <span className="text-amber-400">·</span>{" "}
              <span className="text-zinc-300 font-mono text-xl md:text-2xl align-middle">
                {monthLabel}
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="month-select"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-10 rounded-md border border-zinc-800 bg-zinc-950 text-sm text-zinc-200 px-3 py-1 font-mono focus:outline-none focus:border-amber-500/50"
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <Button
              data-testid="leaderboard-refresh"
              onClick={() => setRefreshTick((t) => t + 1)}
              variant="outline"
              className="h-10 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 gap-2"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {myRank && (
          <div
            data-testid="my-rank-chip"
            className="mt-7 inline-flex items-center gap-2 rounded-full border border-amber-400/60 bg-amber-500/[0.10] px-4 py-1.5 text-sm text-amber-200"
          >
            <Trophy className="h-3.5 w-3.5" />
            Your rank this month: <span className="font-mono font-semibold">#{myRank}</span>
            <span className="text-zinc-500"> · of {entries?.length}</span>
          </div>
        )}

        {loading && (
          <p className="mt-10 text-sm text-zinc-500" data-testid="leaderboard-loading">
            Loading leaderboard…
          </p>
        )}

        {error && (
          <p className="mt-10 text-sm text-red-400" data-testid="leaderboard-error">
            {error}
          </p>
        )}

        {!loading && !error && entries && entries.length === 0 && (
          <div
            data-testid="leaderboard-empty"
            className="mt-14 rounded-2xl border border-zinc-800 bg-zinc-950/40 p-12 text-center"
          >
            <UsersIcon className="mx-auto h-8 w-8 text-zinc-700" />
            <p className="mt-4 text-zinc-300 font-medium">No quests completed this month yet.</p>
            <p className="mt-2 text-sm text-zinc-500">Be the first on the board.</p>
            <Button
              data-testid="leaderboard-empty-cta"
              onClick={() => navigate("/dashboard")}
              className="mt-6 h-10 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
            >
              Start a quest
            </Button>
          </div>
        )}

        {!loading && !error && podium.length > 0 && (
          <section data-testid="podium" className="mt-10 grid md:grid-cols-3 gap-4">
            {podium.map((e) => (
              <PodiumCard key={e.user_id} entry={e} isMe={e.user_id === user.id} />
            ))}
          </section>
        )}

        {!loading && !error && rest.length > 0 && (
          <section data-testid="leaderboard-rows" className="mt-8 space-y-2">
            {rest.map((e) => (
              <Row key={e.user_id} entry={e} isMe={e.user_id === user.id} />
            ))}
          </section>
        )}
      </div>
    </main>
  );
}
